/* eslint-disable no-unused-vars */
import React, { createContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  fetchRoadsGeo,
  fetchRoadsGeoByBbox,
  fetchSidewalksGeo,
  fetchSidewalksGeoByBbox,
  fetchPathsAndCrossingsGeo,
  fetchPathsAndCrossingsGeoByBbox,
} from '../utils/geoUtils';

import { concatGeoJson } from '../utils/calculUtils';
import { lengthSideWalk } from '../utils/lengthUtils';
import { layerConf } from '../enum/layerConfig';
import {
  date12monthsBefore,
  date1weekBefore,
  date6monthsBefore,
} from '../utils/dateUtils';

export const ExploreContext = createContext();

export const ExploreProvider = ({ children, locationFeature }) => {
  const { t } = useTranslation();

  const [status, setStatus] = useState('idle');
  const [base, setBase] = useState('osm-tiles');
  const [idLocation, setIdLocation] = useState(undefined);
  const [layerConfig, setLayerConfig] = useState(layerConf);
  const [valueIsShown, setValueIsShown] = useState(true);
  const [offset, setOffset] = useState(100);
  const [drawnGeo, setDrawnGeo] = useState(null);
  const [describedGeo, setDescribedGeo] = useState(null);
  const [theoreticalGeo, setTheoreticalGeo] = useState(null);
  const [noSideWalkGeo, setNoSideWalkGeo] = useState(null);
  const [pathWalkGeo, setPathWalkGeo] = useState(null);
  const [pathWalkCycleGeo, setPathWalkCycleGeo] = useState(null);
  const [crossingGeo, setCrossingGeo] = useState(null);
  const [crossingWayGeo, setCrossingWayGeo] = useState(null);
  const [drawnLength, setLengthDrawn] = useState(0);
  const [describedLength, setLengthDescribed] = useState(0);
  const [theoreticalLength, setLengthTheoretical] = useState(0);
  const [walkLength, setWalkLength] = useState(0);
  const [walkCycleLength, setWalkCycleLength] = useState(0);
  const [crossPointNumber, setCrossPointNumber] = useState(0);
  const [crossPathNumber, setCrossPathNumber] = useState(0);
  const [isOffset, setIsOffset] = useState(false);
  const [startDate, setStartDate] = useState(undefined);
  const [interval, setInterval] = useState(0);
  const [reCalcActive, setReCalcActive] = useState(false);
  const [expand, setExpand] = useState(true);
  const [content, setContent] = useState('indicator');
  const [noRequest, setNoRequest] = useState(true);
  const [alert, setAlert] = useState(undefined);

  const handleStatePage = (value) => {
    setStatus(value);
  };

  const handleInterval = (value) => {
    setInterval(value);
  };

  const handleSetCalc = (value) => {
    setReCalcActive(value);
  };

  const handleSetLayerConfig = (elem) => {
    if (layerConfig[elem].visibility === 'visible') {
      layerConfig[elem].visibility = 'none';
    } else {
      layerConfig[elem].visibility = 'visible';
    }
    setLayerConfig({ ...layerConfig });
  };

  const handleValueShown = (value) => {
    if (value === 'hide') {
      setValueIsShown(false);
    } else {
      setValueIsShown(true);
    }
  };

  const handleOffset = (value) => {
    setOffset(value);
  };

  const handleIsOffset = () => {
    setIsOffset(true);
  };

  const handleNotOffset = () => {
    setIsOffset(false);
  };

  const handleStartDate = (value) => {
    switch (value) {
      case 0:
        setStartDate(undefined);
        break;
      case 1:
        setStartDate(date1weekBefore);
        break;
      case 2:
        setStartDate(date6monthsBefore);
        break;
      case 3:
        setStartDate(date12monthsBefore);
        break;
      default:
        setStartDate(undefined);
        break;
    }
  };

  const resetDataToInitValue = () => {
    setDrawnGeo(null);
    setDescribedGeo(null);
    setPathWalkGeo(null);
    setPathWalkCycleGeo(null);
    setNoSideWalkGeo(null);
    setCrossingGeo(null);
    setCrossingWayGeo(null);
    setLengthDrawn(0);
    setLengthDescribed(0);
    setLengthTheoretical(0);
    setWalkLength(0);
    setWalkCycleLength(0);
    setCrossPointNumber(0);
    setCrossPathNumber(0);
  };

  const fetchGeoData = async (osmID) => {
    if (!osmID) {
      return;
    }
    try {
      setIdLocation(osmID);
      const { theoretical, noSidewalk } = await fetchRoadsGeo(osmID);
      setTheoreticalGeo(theoretical);
      setLengthTheoretical(lengthSideWalk(theoretical) * 2);
      setNoSideWalkGeo(noSidewalk);

      const [
        { drawn, described, describedBoth },
        { walkPath, walkCyclePath, crossingPoint, crossingPath },
      ] = await Promise.all([
        fetchSidewalksGeo(osmID, startDate),
        fetchPathsAndCrossingsGeo(osmID, startDate),
      ]);

      setDrawnGeo(drawn);
      setLengthDrawn(lengthSideWalk(drawn));
      setLengthDescribed(
        lengthSideWalk(described) + lengthSideWalk(describedBoth) * 2,
      );
      setDescribedGeo(concatGeoJson(described, describedBoth));
      setPathWalkGeo(walkPath);
      setWalkLength(lengthSideWalk(walkPath));
      setPathWalkCycleGeo(walkCyclePath);
      setWalkCycleLength(lengthSideWalk(walkCyclePath));
      setCrossingGeo(crossingPoint);
      setCrossPointNumber(crossingPoint.features.length);
      setCrossingWayGeo(crossingPath);
      setCrossPathNumber(crossingPath.features.length);
    } catch (error) {
      console.error('error fetchGeoData', error);
      setAlert(t('error_load.error_load_data_geo'));
    }
  };

  const fetchGeoDataByBbox = async (bbox) => {
    setStatus('loading');
    if (!bbox) {
      return;
    }
    try {
      if (idLocation) {
        setIdLocation(null);
      }
      const { theoretical, noSidewalk } = await fetchRoadsGeoByBbox(bbox);
      setTheoreticalGeo(theoretical);
      setLengthTheoretical(lengthSideWalk(theoretical) * 2);
      setNoSideWalkGeo(noSidewalk);

      const [
        { drawn, described, describedBoth },
        { walkPath, walkCyclePath, crossingPoint, crossingPath },
      ] = await Promise.all([
        fetchSidewalksGeoByBbox(bbox, startDate),
        fetchPathsAndCrossingsGeoByBbox(bbox, startDate),
      ]);

      setDrawnGeo(drawn);
      setLengthDrawn(lengthSideWalk(drawn));
      setLengthDescribed(
        lengthSideWalk(described) + lengthSideWalk(describedBoth) * 2,
      );
      setDescribedGeo(concatGeoJson(described, describedBoth));
      setPathWalkGeo(walkPath);
      setWalkLength(lengthSideWalk(walkPath));
      setPathWalkCycleGeo(walkCyclePath);
      setWalkCycleLength(lengthSideWalk(walkCyclePath));
      setCrossingGeo(crossingPoint);
      setCrossPointNumber(crossingPoint.features.length);
      setCrossingWayGeo(crossingPath);
      setCrossPathNumber(crossingPath.features.length);
    } catch (error) {
      setAlert(t('error_load.error_load_data_geo_view'));
    } finally {
      setStatus('idle');
    }
  };

  const fetchData = async () => {
    setStatus('loading');
    const osmID = parseInt(locationFeature.properties.ref.split(':r')[1]);
    try {
      await fetchGeoData(osmID);
    } catch (error) {
      console.log('error loading layer updated', error);
      // setAlert();
    } finally {
      setStatus('idle');
    }
  };

  useEffect(() => {
    if (locationFeature) {
      resetDataToInitValue();
      if (noRequest) {
        setNoRequest(false);
      }
      fetchData();
    } else {
      return;
    }
  }, [locationFeature, startDate]);

  useEffect(() => {
    handleStartDate(interval);
  }, [interval]);

  return (
    <ExploreContext.Provider
      value={{
        base,
        status,
        idLocation,
        layerConfig,
        valueIsShown,
        offset,
        drawnGeo,
        describedGeo,
        theoreticalGeo,
        noSideWalkGeo,
        pathWalkGeo,
        pathWalkCycleGeo,
        crossingGeo,
        crossingWayGeo,
        drawnLength,
        describedLength,
        theoreticalLength,
        walkLength,
        walkCycleLength,
        crossPointNumber,
        crossPathNumber,
        isOffset,
        startDate,
        interval,
        reCalcActive,
        expand,
        offsetMap: offset,
        noRequest,
        alert,
        content,
        setBase,
        setIdLocation,
        fetchGeoData,
        fetchGeoDataByBbox,
        setStatus,
        handleSetCalc,
        handleNotOffset,
        handleSetLayerConfig,
        handleStatePage,
        handleInterval,
        handleValueShown,
        handleOffset,
        handleIsOffset,
        handleStartDate,
        handleSetLayer: handleSetLayerConfig,
        setExpand,
        setNoRequest,
        setContent,
        setAlert,
        resetDataToInitValue,
      }}
    >
      {children}
    </ExploreContext.Provider>
  );
};
